/**
 * Vite build-time env with optional Kubernetes runtime override.
 *
 * Deployed admin images write `window.__RUNTIME_CONFIG__` from `RUNTIME_VITE_*`
 * via docker-entrypoint (`/admin/env-config.js`). Map components must read that
 * fallback — `import.meta.env` alone is empty when the key is not baked at build.
 */

type RuntimeConfig = Record<string, string | undefined>;

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

function runtimeValue(key: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const cfg = window.__RUNTIME_CONFIG__;
  if (!cfg || typeof cfg !== 'object') return undefined;
  const v = cfg[key];
  return typeof v === 'string' ? v : undefined;
}

/** Treat unset / placeholder secrets as missing. */
export function isUsableViteEnvValue(value: string | undefined | null): boolean {
  if (typeof value !== 'string') return false;
  const t = value.trim();
  if (!t) return false;
  if (/^PLACEHOLDER$/i.test(t)) return false;
  if (/^CHANGE.?ME$/i.test(t)) return false;
  return true;
}

/**
 * Prefer build-time `import.meta.env.VITE_*`, then runtime `window.__RUNTIME_CONFIG__`.
 * `key` is the short name without the VITE_ prefix in runtime config
 * (RUNTIME_VITE_GOOGLE_MAPS_API_KEY → VITE_GOOGLE_MAPS_API_KEY).
 */
export function readViteEnv(key: `VITE_${string}`): string | undefined {
  // `import.meta.env` is undefined outside Vite (node helper tests).
  const fromBuild = (import.meta.env as Record<string, string | undefined> | undefined)?.[key];
  if (isUsableViteEnvValue(fromBuild)) return fromBuild!.trim();

  const fromRuntime = runtimeValue(key);
  if (isUsableViteEnvValue(fromRuntime)) return fromRuntime!.trim();

  return undefined;
}

export function readGoogleMapsApiKey(): string | undefined {
  return readViteEnv('VITE_GOOGLE_MAPS_API_KEY');
}
